import { useState, type FormEvent } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { signIn, confirmSignIn } from "aws-amplify/auth";
import { Brand } from "@championcare/ui";
import { COGNITO_ENABLED } from "../config";
import { useAppAuth } from "../auth";

// Cognito sign-in (custom form — no hosted UI, no social IdPs). signIn drives SRP; the bridge's Hub
// listener flips the auth context on the `signedIn` event, and the isSignedIn redirect takes over.
// nextStep handling: TOTP challenge renders a code step; an unconfirmed account is handed off to
// the create-account confirm step.
function CognitoSignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [step, setStep] = useState<"credentials" | "totp">("credentials");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { nextStep } = await signIn({ username: email.trim(), password });
      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        setStep("totp");
      } else if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        // Account exists but the email was never verified — finish confirmation first.
        navigate("/create-account", { state: { confirmEmail: email.trim() } });
      }
      // On DONE the Hub `signedIn` event updates the context and the redirect above unmounts us.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function submitTotp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmSignIn({ challengeResponse: totpCode.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (step === "totp") {
    return (
      <div className="pt-auth">
        <div className="pt-auth__card">
          <div className="pt-auth__brand">
            <Brand size={28} />
          </div>
          <h1 className="pt-auth__title">
            Two-factor <em>check.</em>
          </h1>
          <p className="pt-auth__lede">Enter the 6-digit code from your authenticator app.</p>
          <form onSubmit={submitTotp}>
            <div className="pt-field">
              <label htmlFor="totp">Authenticator code</label>
              <input
                id="totp"
                className="pt-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            {error && (
              <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
            )}
            <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy || totpCode.length < 6}>
              {busy ? "Verifying…" : "Verify →"}
            </button>
          </form>
          <p className="pt-auth__alt">
            <Link to="/sign-in" className="pt-auth__link" onClick={() => setStep("credentials")}>&larr; Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Welcome <em>back.</em>
        </h1>
        <p className="pt-auth__lede">Sign in to manage your visits and messages.</p>

        <form onSubmit={submitCredentials}>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="pt-input"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="pt-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="pt-input"
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
          )}
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in →"}
          </button>
        </form>

        <Link to="/reset-password" className="pt-auth__link pt-auth__forgot">
          Forgot password?
        </Link>
        <p className="pt-auth__alt">
          Don&rsquo;t have an account? <Link to="/create-account" className="pt-auth__link">Create one</Link>
        </p>
      </div>
    </div>
  );
}

export function SignIn() {
  const { isLoaded, isSignedIn, devSignIn } = useAppAuth();
  const [busy, setBusy] = useState(false);

  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  if (COGNITO_ENABLED) return <CognitoSignIn />;

  // On dev-sbx1 there is no Cognito — the email/password are presentational and every path resolves
  // to the same anonymous dev session.
  const dev = async () => {
    setBusy(true);
    await devSignIn?.();
    setBusy(false);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void dev();
  };

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Welcome <em>back.</em>
        </h1>
        <p className="pt-auth__lede">Sign in to manage your visits and messages.</p>

        <form onSubmit={onSubmit}>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input id="email" className="pt-input" type="email" autoComplete="email" placeholder="you@example.com" />
          </div>
          <div className="pt-field">
            <label htmlFor="password">Password</label>
            <input id="password" className="pt-input" type="password" autoComplete="current-password" placeholder="••••••••" />
          </div>
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Signing in…" : "Sign in →"}
          </button>
        </form>

        <Link to="/reset-password" className="pt-auth__link pt-auth__forgot">
          Forgot password?
        </Link>
        <p className="pt-auth__alt">
          Don&rsquo;t have an account? <Link to="/create-account" className="pt-auth__link">Create one</Link>
        </p>
        <p className="pt-auth__note">
          Dev environment — sign-in is simulated with a local session (no Cognito configured).
        </p>
      </div>
    </div>
  );
}
